import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '@/components/ui/pill-tab';
import { cn } from '@/lib/utils';

/**
 * 값 하나를 고치는 작은 팝업. 프로필의 「수정」이 연다.
 * 제목 · 입력 자리(children) · 취소 | 저장. 저장은 사이트 알약(pill-tab)이다. 머리(z-500) 위에 뜬다.
 */
export function EditDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSave,
  saving = false,
  canSave = true,
  error,
  saveLabel = '저장',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  onSave: () => void;
  saving?: boolean;
  canSave?: boolean;
  error?: string | null;
  /** 저장 알약의 글자. 기본 「저장」 — 신고 팝업은 「신고」. */
  saveLabel?: string;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[1000] bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[1001] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave && !saving && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              onSave();
            }
          }}
        >
          <DialogPrimitive.Title className="text-base font-bold text-text">{title}</DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="mt-1 text-xs text-text-muted">{description}</DialogPrimitive.Description>
          ) : null}
          <div className="mt-4">{children}</div>
          {error ? (
            <p className="mt-3 text-xs text-danger" role="alert">{error}</p>
          ) : null}
          <div className="mt-5 flex items-center justify-end gap-2">
            <DialogPrimitive.Close className={cn(PILL_TAB, PILL_TAB_OFF, 'border border-gray-200')}>취소</DialogPrimitive.Close>
            <button
              type="button"
              className={cn(PILL_TAB, PILL_TAB_ON, 'disabled:opacity-50')}
              disabled={!canSave || saving}
              onClick={onSave}
            >
              {saving ? '보내는 중…' : saveLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
